import React from 'react';
import {Navigate, useParams} from 'react-router-dom';
import projectsData from '../utils/projectsData';
import ProjectsPage from './ProjectsPage';
import ResponsiveImage from '../components/ui/ResponsiveImage';
import StyledButton from '../components/ui/StyledButton'; // Import StyledButton
import PageMeta from '../components/PageMeta';

// Note: Individual project pages are now lazy-loaded in App.jsx routes
// This component only handles the generic fallback case

const ProjectPage = () => {
    const { projectId } = useParams();

    // If no projectId, show the projects listing
    if (!projectId) {
        return <ProjectsPage />;
    }

    // Find the project in the data
    const project = projectsData.find(p => p.id === projectId);

    // If project isn't found, redirect to 404
    if (!project) {
        return <Navigate to="/404" replace />;
    }

    // For projects with dedicated pages, they should have dedicated routes in App.jsx
    // This fallback renders a generic page for projects without custom pages
    return <GenericProjectPage project={project} />;
};

// Generic project display component for projects without dedicated pages yet
const GenericProjectPage = ({ project }) => {
    return (
        <div style={{ maxWidth: '1200px', margin: '50px auto', padding: '0 20px' }}>
            <PageMeta
                title={project.title}
                description={project.description || `${project.title} - A project by Lucas Perron.`}
                canonicalPath={`/projects/${project.id}`}
            />
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                <h1 style={{ fontSize: '48px', marginBottom: '20px' }}>{project.title}</h1>
                <p style={{ color: 'var(--text-muted)', maxWidth: '800px', margin: '0 auto', marginBottom: '30px' }}>{project.description}</p>
                
                {project.externalLink && (
                    <div style={{ marginBottom: '20px' }}>
                        <StyledButton href={project.externalLink} target="_blank" rel="noopener noreferrer">
                            View Source
                        </StyledButton>
                    </div>
                )}
            </div>

            <div style={{ border: '1px solid var(--grid-color)', padding: '40px', marginBottom: '40px' }}>
                <h2 style={{ color: 'var(--primary-color)', marginBottom: '20px' }}>About This Project</h2>
                <div style={{ whiteSpace: 'pre-line', color: '#ccc', lineHeight: '1.6' }}>
                    {project.fullDescription || "Detailed information about this project is coming soon."}
                </div>
            </div>

            {project.imageSrc && (
                <div style={{ marginBottom: '40px', textAlign: 'center' }}>
                    <ResponsiveImage
                        src={project.imageSrc}
                        alt={project.title}
                        style={{ maxWidth: '100%', border: '1px solid var(--grid-color)' }}
                        sizes="(max-width: 1200px) 100vw, 1200px"
                    />
                </div>
            )}

            <div style={{ border: '1px solid var(--grid-color)', padding: '20px', marginBottom: '40px' }}>
                <h3 style={{ color: 'var(--primary-color)', marginBottom: '15px' }}>Project Details</h3>
                <ul style={{ listStyle: 'none', padding: '0' }}>
                    <li style={{ margin: '10px 0', color: 'var(--text-muted)' }}>
                        <strong style={{ color: '#fff', marginRight: '10px' }}>Category:</strong>
                        {project.category}
                    </li>
                    <li style={{ margin: '10px 0', color: 'var(--text-muted)' }}>
                        <strong style={{ color: '#fff', marginRight: '10px' }}>Year:</strong>
                        {project.year || 'Ongoing'}
                    </li>
                    <li style={{ margin: '10px 0', color: 'var(--text-muted)' }}>
                        <strong style={{ color: '#fff', marginRight: '10px' }}>Technologies:</strong>
                        {project.tags.join(', ')}
                    </li>
                </ul>
            </div>
            
            <div style={{ textAlign: 'center', marginTop: '60px' }}>
                 {project.externalLink && (
                    <div style={{ marginBottom: '30px' }}>
                        <StyledButton href={project.externalLink} target="_blank" rel="noopener noreferrer">
                            View Source
                        </StyledButton>
                    </div>
                )}
                <p style={{ color: '#888', fontSize: '14px' }}>
                    A dedicated page for this project is under development. Check back soon for more details!
                </p>
            </div>
        </div>
    );
};

export default ProjectPage;